import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  @Input('appTooltip') tooltipText: string = '';
  @Input() position: 'top' | 'right' | 'bottom' | 'left' = 'top';
  @Input() tooltipClass: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
  
  private readonly unlisteners: (() => void)[] = [];

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (!this.tooltipText) return;
    
    // Set data attribute for CSS selector
    this.renderer.setAttribute(this.el.nativeElement, 'data-tooltip', this.tooltipText);
    
    // Add position class
    this.renderer.addClass(this.el.nativeElement, `t-${this.position}`);
    
    // Add size class if provided
    if (this.tooltipClass) {
      this.renderer.addClass(this.el.nativeElement, `t-${this.tooltipClass}`);
    }
    
    // Make element focusable if it isn't already
    if (!this.el.nativeElement.getAttribute('tabindex')) {
      this.renderer.setAttribute(this.el.nativeElement, 'tabindex', '0');
    }
    
    // Add keyboard support for accessibility
    const keydownListener = this.renderer.listen(this.el.nativeElement, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        // Toggle a class to simulate hover for keyboard users
        this.renderer.addClass(this.el.nativeElement, 'tooltip-focus');
      }
    });
    
    const keyupListener = this.renderer.listen(this.el.nativeElement, 'keyup', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
        this.renderer.removeClass(this.el.nativeElement, 'tooltip-focus');
      }
    });
    
    const blurListener = this.renderer.listen(this.el.nativeElement, 'blur', () => {
      this.renderer.removeClass(this.el.nativeElement, 'tooltip-focus');
    });
    
    this.unlisteners.push(keydownListener, keyupListener, blurListener);
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    this.unlisteners.forEach(unlisten => unlisten());
    
    // Remove added attributes and classes
    this.renderer.removeAttribute(this.el.nativeElement, 'data-tooltip');
    this.renderer.removeClass(this.el.nativeElement, `t-${this.position}`);
    if (this.tooltipClass) {
      this.renderer.removeClass(this.el.nativeElement, `t-${this.tooltipClass}`);
    }
  }
} 